import type { DiagnosticMessage, DiagnosticConversationPayload } from '../types/diagnostic';
import type { CogniPrintProfile, DiagnosticDimensionKey, AnchorItemResponse } from '../types/cogniprint';
import { validateProfile } from '../utils/profile-validation';
import {
  ANCHOR_SET_VERSION,
  anchorItemById,
  anchorItemsForMode,
  reserveItemFor,
  type AnchorItem,
} from '../diagnostic/anchor-items';
import { EVIDENCE_STATUS_VERSION } from '../constants/evidence-status';
import { scoreAnchorResponses } from '../diagnostic/anchor-scoring';
import { callClaude } from './claude';
import {
  PE_001_SYSTEM_PROMPT,
  PE_003_SYSTEM_PROMPT,
  buildDiagnosticOpener,
  buildDiagnosticTransition,
  buildDiagnosticClosing,
  buildAnswerResolution,
} from '../ai/prompts/profile-engine';

function generateId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}


// ─── The anchored session ───────────────────────────────────────────────────
//
// THE APP OWNS THE SEQUENCE. This is the whole design, and it is a correction.
//
// An earlier version let the model relay the fixed items and tag which one it was
// on, with the app substituting canonical option labels behind it. A live run on
// 2026-09-05 showed why that cannot work: on turn two the model wrote a question of
// its own and tagged it `cf-1` — the item it had already asked — so the app pasted
// cf-1's five options underneath an unrelated question. The learner saw a
// mismatched question and answers, and their tap would have been scored against the
// wrong dimension. The model is not a reliable source of "which item am I on", and
// it does not need to be: the app knows exactly how many items it has presented.
//
// So now:
//   • the app picks the next item — index = number of answers recorded;
//   • the app prints the stem and the options, straight from the bank;
//   • the model writes the opener, the reactions and the transitions, and nothing
//     else. It cannot mis-tag an item because it never names one.
//   • scoring reads the app's own record of what it showed and what was tapped.

/** What the app knows about a session in flight. The caller owns this. */
export interface AnchoredProgress {
  mode: 'quick' | 'full';
  /** One entry per item already presented AND resolved, in presentation order. */
  responses: AnchorItemResponse[];
  /** Items whose clarifying follow-up has already been spent. */
  followedUp?: string[];
  /**
   * Set while a held-back reserve item is on screen (the dispute flow). When set,
   * it — not the main sequence — is the item an answer is recorded against.
   */
  activeItemId?: string | null;
  /** Dimensions that have already used their one extra question. */
  disputed?: DiagnosticDimensionKey[];
}

export type AnchoredTurn =
  /** Present this item. `message.content` already contains narration + stem. */
  | { kind: 'item'; message: DiagnosticMessage; itemId: string; index: number; total: number }
  /** The answer did not map to an option; the model asks once more about the SAME item. */
  | { kind: 'follow_up'; message: DiagnosticMessage; itemId: string }
  /** Every item is done and the profile passed the gate. */
  | { kind: 'complete'; message: DiagnosticMessage; profile: CogniPrintProfile }
  /** Every item is done but the profile did not pass. Never persist this. */
  | { kind: 'invalid'; message: DiagnosticMessage; reasons: string[] };

function itemMessage(rawNarration: string, item: AnchorItem): DiagnosticMessage {
  // The stem comes from the bank, never from the model. This is the line that makes
  // a mismatched question/options pair structurally impossible.
  const narration = withinNarrationBudget(withoutQuestions(rawNarration));
  const content = narration ? `${narration}\n\n${item.stem}` : item.stem;
  return {
    id: generateId(),
    role: 'assistant',
    content,
    timestamp: new Date().toISOString(),
    options: item.options.map((o) => ({ id: o.id, label: o.label })),
    anchorItemId: item.id,
  };
}

function plainMessage(content: string): DiagnosticMessage {
  return {
    id: generateId(),
    role: 'assistant',
    content: content.trim(),
    timestamp: new Date().toISOString(),
  };
}

function systemPromptFor(mode: 'quick' | 'full'): string {
  return mode === 'quick' ? PE_001_SYSTEM_PROMPT : PE_003_SYSTEM_PROMPT;
}

/**
 * Strip any stray control tags or data blocks the model emits despite instructions.
 *
 * Handles UNCLOSED blocks too: in the 2026-09-05 run the model's closing turn ended
 * with `<anchor_responses> {"items":[…` in a made-up schema and no closing tag, and a
 * stripper that only removed closed blocks let the whole thing through to the
 * learner. Anything from an opening control tag to the end of the text goes.
 */
function narrationOnly(text: string): string {
  return text
    // closed blocks
    .replace(/<options>[\s\S]*?<\/options>/g, '')
    .replace(/<anchor_responses>[\s\S]*?<\/anchor_responses>/g, '')
    .replace(/<cogniprint_profile>[\s\S]*?<\/cogniprint_profile>/g, '')
    // unclosed blocks: from the tag to the end
    .replace(/<(options|anchor_responses|cogniprint_profile|item|resolved|unresolved)\b[\s\S]*$/g, '')
    // any remaining lone tag
    .replace(/<\/?[a-z_]+(\s+[a-z_]+="[^"]*")*\s*\/?>/gi, '')
    .trim();
}

/**
 * Remove any sentence that is itself a question.
 *
 * Narration sits directly above the item's own stem, so a question in it is either
 * a duplicate of the real question or — as happened in the 2026-09-05 run — a
 * different question entirely, which reads as two questions and one set of answers.
 * A reaction and a transition never need a question mark, so dropping them costs
 * nothing and closes the last way a stray model question reaches the learner.
 */
function withoutQuestions(text: string): string {
  const sentences = text.match(/[^.!?]+[.!?]*/g) ?? [];
  const kept = sentences.filter((sentence) => !sentence.trim().endsWith('?'));
  return kept.join('').replace(/\s+/g, ' ').trim();
}

/**
 * The narration budget: at most two sentences.
 *
 * Every item carries its own scenario in its stem, so a transition has nothing to
 * set up. Left longer, the model starts writing scene-setting for a question it is
 * not asking — in the 2026-09-05 run it introduced "you're reading an article that
 * goes deeper than you expected" immediately above an item about using a tool. Two
 * sentences is enough for a reaction and a bridge, and too few to build a scene.
 */
const MAX_NARRATION_SENTENCES = 2;

function withinNarrationBudget(text: string): string {
  const sentences = text.match(/[^.!?]+[.!?]+/g);
  if (!sentences || sentences.length <= MAX_NARRATION_SENTENCES) return text.trim();
  return sentences.slice(0, MAX_NARRATION_SENTENCES).join('').replace(/\s+/g, ' ').trim();
}

/** The item to present next, given what has been answered. Null when finished. */
export function nextAnchorItem(progress: AnchoredProgress): AnchorItem | null {
  const items = anchorItemsForMode(progress.mode);
  return items[mainSequenceCount(progress)] ?? null;
}

/** Answers to main-sequence items only; reserve answers do not advance the sequence. */
function mainSequenceCount(progress: AnchoredProgress): number {
  return progress.responses.filter((r) => !anchorItemById(r.itemId)?.reserve).length;
}

/** Open the conversation and present the first item. */
export async function startAnchoredDiagnostic(
  payload: DiagnosticConversationPayload,
): Promise<AnchoredTurn> {
  const items = anchorItemsForMode(payload.mode);
  const first = items[0];

  let narration = '';
  try {
    narration = narrationOnly(
      await callClaude(
        systemPromptFor(payload.mode),
        [{ role: 'user', content: buildDiagnosticOpener(payload) }],
        { model: 'sonnet', maxTokens: 512 },
      ),
    );
  } catch {
    // The conversation is the model's job; the instrument is not. If narration
    // fails the diagnostic still works — it is just less warm.
    narration = '';
  }

  return {
    kind: 'item',
    message: itemMessage(narration, first),
    itemId: first.id,
    index: 0,
    total: items.length,
  };
}

/** Map a typed answer onto one of the item's options. Returns null if it cannot. */
async function resolveFreeText(
  payload: DiagnosticConversationPayload,
  item: AnchorItem,
  answer: string,
): Promise<string | null> {
  const direct = item.options.find(
    (o) => o.label.toLowerCase() === answer.trim().toLowerCase() || o.id === answer.trim(),
  );
  if (direct) return direct.id;

  try {
    const reply = await callClaude(
      systemPromptFor(payload.mode),
      [{ role: 'user', content: buildAnswerResolution(item.stem, item.options, answer) }],
      { model: 'haiku', maxTokens: 64 },
    );
    const match = reply.match(/<resolved\s+id="([^"]+)"\s*\/?>/);
    if (!match) return null;
    // The model reports; the bank decides. An id the item does not have is not an answer.
    return item.options.some((o) => o.id === match[1]) ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Record an answer to the current item and produce the next turn.
 *
 * `optionId` is set when the learner tapped an option — the normal path, and the one
 * where no model call is needed to know what they meant.
 */
export async function submitAnchoredAnswer(
  payload: DiagnosticConversationPayload,
  progress: AnchoredProgress,
  answer: { optionId?: string; text: string },
): Promise<{ turn: AnchoredTurn; progress: AnchoredProgress }> {
  const items = anchorItemsForMode(progress.mode);
  const reserve = progress.activeItemId ? anchorItemById(progress.activeItemId) : undefined;
  const current = reserve ?? items[mainSequenceCount(progress)];
  if (!current) {
    // Nothing left to answer; fall through to completion.
    return { turn: await finishAnchoredDiagnostic(payload, progress, answer.text), progress };
  }

  let optionId: string | null =
    answer.optionId && current.options.some((o) => o.id === answer.optionId)
      ? answer.optionId
      : null;

  if (!optionId) optionId = await resolveFreeText(payload, current, answer.text);

  const followedUp = progress.followedUp ?? [];
  if (!optionId && !followedUp.includes(current.id)) {
    // One clarifying follow-up per item, then we let it go rather than badger.
    let narration = "That's a mix — if you had to pick the one that fits best?";
    try {
      narration =
        narrationOnly(
          await callClaude(
            systemPromptFor(progress.mode),
            [
              ...payload.conversationHistory,
              {
                role: 'user',
                content: `The learner answered "${answer.text}", which does not clearly match any option. Write ONE short, warm line asking them to pick the closest option. Do not restate the question and do not list the options — they are still on screen.`,
              },
            ],
            { model: 'sonnet', maxTokens: 256 },
          ),
        ) || narration;
    } catch { /* keep the fallback line */ }

    return {
      turn: { kind: 'follow_up', message: { ...plainMessage(narration), options: current.options.map((o) => ({ id: o.id, label: o.label })), anchorItemId: current.id }, itemId: current.id },
      progress: { ...progress, followedUp: [...followedUp, current.id] },
    };
  }

  // Record against the item the app actually presented — not against anything the
  // model claimed. This is the record that gets scored.
  const recorded: AnchorItemResponse = {
    itemId: current.id,
    anchorSetVersion: ANCHOR_SET_VERSION,
    dimension: current.dimension,
    optionId,
    skipped: optionId == null,
    respondedAt: new Date().toISOString(),
  };
  const next: AnchoredProgress = {
    ...progress,
    responses: [...progress.responses, recorded],
    activeItemId: null,
  };

  // A reserve item answers a dispute: the main sequence is already complete, so
  // re-score straight away. The dimension now has four items behind it, and the
  // number comes from those four — never from the conversation.
  if (reserve) {
    return { turn: await finishAnchoredDiagnostic(payload, next, answer.text), progress: next };
  }

  const upcoming = items[mainSequenceCount(next)];
  if (!upcoming) {
    return { turn: await finishAnchoredDiagnostic(payload, next, answer.text), progress: next };
  }

  let narration = '';
  try {
    narration = narrationOnly(
      await callClaude(
        systemPromptFor(progress.mode),
        [
          ...payload.conversationHistory,
          {
            role: 'user',
            content: buildDiagnosticTransition(answer.text, mainSequenceCount(next) === items.length - 1),
          },
        ],
        { model: 'sonnet', maxTokens: 256 },
      ),
    );
  } catch {
    narration = '';
  }

  return {
    turn: {
      kind: 'item',
      message: itemMessage(narration, upcoming),
      itemId: upcoming.id,
      index: mainSequenceCount(next),
      total: items.length,
    },
    progress: next,
  };
}

/**
 * A learner says a result doesn't feel right. This is the ONLY way a result changes
 * after the fact, and it changes by evidence: the app presents the dimension's
 * held-back reserve item, the learner answers it, and the dimension is re-scored
 * over four items instead of three. Nothing is set by hand. One dispute per
 * dimension — the reserve is a single item, and re-asking the same question is not
 * more evidence.
 *
 * Returns null if the dimension has no reserve or has already been disputed.
 */
export async function disputeDimension(
  payload: DiagnosticConversationPayload,
  progress: AnchoredProgress,
  dimension: DiagnosticDimensionKey,
  learnerWords: string,
): Promise<{ turn: AnchoredTurn; progress: AnchoredProgress } | null> {
  if (progress.disputed?.includes(dimension)) return null;
  const item = reserveItemFor(dimension);
  if (!item) return null;

  let narration = "Fair enough — let me ask you one more about that, and I'll rework it from what you tell me.";
  try {
    narration =
      withinNarrationBudget(withoutQuestions(narrationOnly(
        await callClaude(
          systemPromptFor(progress.mode),
          [
            ...payload.conversationHistory,
            {
              role: 'user',
              content: `The learner has read their profile and says this part doesn't feel right: "${learnerWords}". Write ONE short, warm line that takes that seriously and says you'll ask one more question about it. Do NOT defend the result, do NOT ask the question yourself, do NOT restate what they said — the app prints the question directly beneath your words.`,
            },
          ],
          { model: 'sonnet', maxTokens: 200 },
        ),
      ))) || narration;
  } catch { /* keep the fallback line */ }

  const next: AnchoredProgress = {
    ...progress,
    activeItemId: item.id,
    disputed: [...(progress.disputed ?? []), dimension],
  };

  return {
    turn: {
      kind: 'item',
      message: itemMessage(narration, item),
      itemId: item.id,
      index: mainSequenceCount(progress),
      total: anchorItemsForMode(progress.mode).length,
    },
    progress: next,
  };
}

/**
 * Route a learner's free-text feedback on their report. Either it is about ONE
 * dimension — in which case the dispute flow asks one more question about it — or
 * it is about the wording, in which case the summary is rewritten. Those are the
 * two things a learner can change, and neither lets them set a number.
 */
export async function classifyLearnerNote(
  payload: DiagnosticConversationPayload,
  note: string,
): Promise<DiagnosticDimensionKey | 'summary'> {
  const keys: DiagnosticDimensionKey[] = [
    'contentFormat', 'learningApproach', 'depthTolerance', 'sequencing',
    'infoDensity', 'motivation', 'viewpointRange',
  ];
  try {
    const reply = await callClaude(
      systemPromptFor(payload.mode),
      [{
        role: 'user',
        content: `A learner has read their profile and typed: "${note}"

Which ONE of these is it mainly about?
- contentFormat: how they like information presented (pictures, reading, listening, doing, video)
- learningApproach: understand-first vs try-first; watching vs doing
- depthTolerance: how much detail they want
- sequencing: big picture first / basics first / jump to what they need
- infoDensity: how much at once
- motivation: what keeps them going
- viewpointRange: one clear answer vs several sides
- summary: the wording or tone of the write-up, or nothing specific

Reply with ONLY the key. No other text.`,
      }],
      { model: 'haiku', maxTokens: 12 },
    );
    const key = reply.trim().replace(/[^a-zA-Z]/g, '');
    return (keys as string[]).includes(key) ? (key as DiagnosticDimensionKey) : 'summary';
  } catch {
    return 'summary';
  }
}

/** Score, validate and close. No model call touches a number here. */
async function finishAnchoredDiagnostic(
  payload: DiagnosticConversationPayload,
  progress: AnchoredProgress,
  lastAnswer: string,
): Promise<AnchoredTurn> {
  let closing = "That's everything I need — your CogniPrint is ready.";
  try {
    closing =
      withinNarrationBudget(withoutQuestions(narrationOnly(
        await callClaude(
          systemPromptFor(progress.mode),
          [...payload.conversationHistory, { role: 'user', content: buildDiagnosticClosing(lastAnswer) }],
          { model: 'sonnet', maxTokens: 256 },
        ),
      ))) || closing;
  } catch { /* keep the fallback line */ }

  const profile = buildAnchoredProfile(progress.responses, payload.userId, progress.mode);
  const validation = validateProfile(profile, progress.mode);
  if (!validation.ok) {
    return { kind: 'invalid', message: plainMessage(closing), reasons: validation.reasons };
  }
  return { kind: 'complete', message: plainMessage(closing), profile };
}

/**
 * Build a profile from anchor responses. Deterministic: the model contributed the
 * conversation, not a single number.
 */
export function buildAnchoredProfile(
  responses: AnchorItemResponse[],
  userId: string,
  mode: 'quick' | 'full',
): CogniPrintProfile {
  const parts = scoreAnchorResponses(responses, mode);
  const now = new Date().toISOString();
  return {
    profileVersion: 1,
    diagnosticType: mode,
    userId,
    createdAt: now,
    lastUpdated: now,
    contentFormat: parts.contentFormat,
    learningApproach: parts.learningApproach,
    depthTolerance: parts.depthTolerance,
    sequencing: parts.sequencing,
    infoDensity: parts.infoDensity,
    motivation: parts.motivation,
    viewpointRange: parts.viewpointRange,
    questionsAsked: parts.questionsAsked,
    questionsCarriedForward: 0,
    overallConfidence: parts.overallConfidence,
    archetypeMatch: null,
    adaptiveNotes: '',
    summary: null,
    dimensionProvenance: parts.dimensionProvenance,
    anchorSetVersion: parts.anchorSetVersion,
    itemResponses: responses,
    evidenceStatusVersion: EVIDENCE_STATUS_VERSION,
  };
}

// anchorItemById is still used by callers that need to look up what was presented.
export { anchorItemById };
